/**
 * Surface Renderer Component
 * Phase 3: Shell Layer - Surface Rendering Component
 * 
 * Renders surface components with proper props and actions.
 * Handles surface loading states and error boundaries.
 */

import React from 'react';
import { Flex, Stack, Button, Badge, Group } from '@mantine/core';
import type { SurfaceDefinition } from '@/core/surfaces/types';

interface SurfaceRendererProps {
  surface: SurfaceDefinition | null;
  props?: Record<string, any>;
  actions?: any[];
  currentPageId?: string | undefined;
}

const SurfaceRenderer: React.FC<SurfaceRendererProps> = ({ 
  surface, 
  props = {}, 
  actions = [], 
  currentPageId 
}) => {
  if (!surface) {
    return (
      <Flex h="100%" align="center" justify="center">
        <div>No surface selected</div>
      </Flex>
    );
  }

  if (!surface.component) {
    return (
      <Flex h="100%" align="center" justify="center">
        <div>Surface component not found</div>
      </Flex>
    );
  }

  return (
    <Flex h="100%" direction="column">
      <Flex h="100%" direction="column">
        {surface.component && (
          <surface.component 
            phase={surface.phase} 
            {...props} 
            currentPageId={currentPageId}
          />
        )}
      </Flex>
      
      {actions.length > 0 && (
        <Group gap="xs" mt="md" wrap="wrap">
          {actions.map((button: any) => (
            <Button
              key={button.id}
              variant="default"
              leftSection={button.icon}
              onClick={button.onClick}
              size="sm"
            >
              {button.label}
              <Badge size="xs" variant="light" ml="xs">
                {button.phase}
              </Badge>
            </Button>
          ))}
        </Group>
      )}
    </Flex>
  );
};

export default SurfaceRenderer;